import React, { useState, useEffect, useMemo } from 'react';
import { ChevronRight, ChevronDown, Cpu, Layers, GitBranch } from 'lucide-react';

/**
 * Builds a hierarchical tree from the work order hierarchy paths under a given line.
 */
function buildTreeFromWorkOrders(lineCode, workOrders) {
    if (!workOrders || workOrders.length === 0) return [];

    const rootNodes = [];
    const nodeMap = new Map();

    workOrders.forEach(wo => {
        const hierarchy = wo.hierarchical || [];
        // Find position of the line in the hierarchy path
        const lineIdx = hierarchy.indexOf(lineCode);
        //Obtener jerarquía a partir de la línea padre
        const subpath = lineIdx !== -1 ? hierarchy.slice(lineIdx + 1) : hierarchy;

        if (subpath.length === 0) {
            // WO was targeted at the line level itself
            return;
        }

        let currentChildren = rootNodes;
        let pathKey = '';

        subpath.forEach((code, index) => {
            pathKey = pathKey ? `${pathKey}/${code}` : code;
            const isLeaf = index === subpath.length - 1;

            let existingNode = nodeMap.get(pathKey);
            if (!existingNode) {
                existingNode = {
                    id: pathKey,
                    code: code,
                    name: isLeaf ? (wo.asset?.name || wo.asset_name || code) : code,
                    directWOs: 0,
                    totalWOs: 0,
                    children: []
                };
                nodeMap.set(pathKey, existingNode);
                currentChildren.push(existingNode);
            }

            existingNode.totalWOs += 1;
            if (isLeaf) {
                existingNode.directWOs += 1;
                if (wo.asset?.name) {
                    existingNode.name = wo.asset.name;
                }
            }

            currentChildren = existingNode.children;
        });
    });

    const sortTree = (nodes) => {
        nodes.sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));
        nodes.forEach(node => {
            if (node.children.length > 0) {
                sortTree(node.children);
            }
        });
    };

    sortTree(rootNodes);
    return rootNodes;
}

export const KpiAssetTree = ({ lineCode, executedWorkOrders, selectedAssetCode, onSelectAssetCode }) => {
    const treeData = useMemo(() => {
        return buildTreeFromWorkOrders(lineCode, executedWorkOrders);
    }, [lineCode, executedWorkOrders]);

    const [expandedNodes, setExpandedNodes] = useState(() => {
        const allIds = new Set();
        const collect = (nodes) => {
            nodes.forEach(n => {
                allIds.add(n.id);
                if (n.children?.length) collect(n.children);
            });
        };
        collect(treeData);
        return allIds;
    });

    // Auto-expand all when treeData changes
    useEffect(() => {
        const allIds = new Set();
        const collect = (nodes) => {
            nodes.forEach(n => {
                allIds.add(n.id);
                if (n.children?.length) collect(n.children);
            });
        };
        collect(treeData);
        setExpandedNodes(allIds);
    }, [treeData]);

    const toggleNode = (nodeId, e) => {
        e.stopPropagation();
        setExpandedNodes(prev => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            } else {
                next.add(nodeId);
            }
            return next;
        });
    };

    const getLevelColor = (level) => {
        switch (level) {
            case 0: return 'text-blue-400';
            case 1: return 'text-emerald-400';
            case 2: return 'text-amber-400';
            default: return 'text-purple-400';
        }
    };

    const renderNode = (node, level = 0) => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedNodes.has(node.id);
        const isSelected = selectedAssetCode === node.code;
        const indent = level * 14;

        return (
            <div key={node.id} className="select-none">
                <div
                    onClick={(e) => {
                        e.stopPropagation();
                        onSelectAssetCode(node.code, e);
                    }}
                    style={{ paddingLeft: `${4 + indent}px` }}
                    className={`flex items-center justify-between py-1 px-2 rounded-md cursor-pointer text-xs transition-colors group ${isSelected
                            ? 'bg-blue-600/25 border-l-2 border-blue-400 text-white font-medium shadow-sm'
                            : 'hover:bg-gray-800/70 text-gray-300'
                        }`}
                >
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        {hasChildren ? (
                            <button
                                type="button"
                                onClick={(e) => toggleNode(node.id, e)}
                                className="p-0.5 hover:bg-gray-700/60 rounded text-gray-400 hover:text-white transition-colors"
                            >
                                {isExpanded ? (
                                    <ChevronDown size={12} className="text-gray-400 shrink-0" />
                                ) : (
                                    <ChevronRight size={12} className="text-gray-400 shrink-0" />
                                )}
                            </button>
                        ) : (
                            <div className="w-4 shrink-0 flex items-center justify-center">
                                <span className="w-1.5 h-1.5 rounded-full bg-gray-600 group-hover:bg-blue-400 transition-colors" />
                            </div>
                        )}

                        <span className={`font-mono truncate ${isSelected ? 'text-blue-300 font-bold' : getLevelColor(level)}`}>
                            {node.code}
                        </span>

                        {node.name && node.name !== node.code && (
                            <span className="text-[11px] text-gray-500 truncate max-w-[140px] group-hover:text-gray-400">
                                • {node.name}
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0 ml-2">
                        <span
                            className={`px-1.5 py-0.5 text-[10px] font-mono font-bold rounded ${isSelected
                                    ? 'bg-blue-500 text-white shadow-xs'
                                    : 'bg-gray-800/90 text-gray-300 border border-gray-700/60'
                                }`}
                            title={`${node.totalWOs} orden(es) de trabajo en este activo o subcomponentes`}
                        >
                            {node.totalWOs} OT{node.totalWOs !== 1 ? 's' : ''}
                        </span>
                    </div>
                </div>

                {hasChildren && isExpanded && (
                    <div className="border-l border-gray-800/80 ml-2 pl-0.5 space-y-0.5">
                        {node.children.map(child => renderNode(child, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    if (treeData.length === 0) {
        return (
            <div className="p-2.5 text-center text-xs text-gray-500 italic bg-gray-900/60 rounded-lg border border-gray-800">
                Todas las intervenciones registradas son a nivel general de la línea.
            </div>
        );
    }

    return (
        <div className="space-y-1 bg-gray-900/70 p-2 rounded-xl border border-gray-800/90 max-h-60 overflow-y-auto">
            <div className="flex items-center justify-between pb-1.5 mb-1 border-b border-gray-800/70 text-[11px] text-gray-400 px-1">
                <span className="flex items-center gap-1 font-semibold uppercase tracking-wider text-[10px]">
                    <GitBranch size={12} className="text-blue-400" /> Jerarquía de Intervenciones
                </span>
                <span className="text-[10px] text-gray-500">
                    {treeData.length} {treeData.length === 1 ? 'rama' : 'ramas'}
                </span>
            </div>
            {treeData.map(rootNode => renderNode(rootNode, 0))}
        </div>
    );
};
